/**
 * limbAxisCentre — the 'limb-axis' recentre method (unbind-adjust-rebind tier
 * 2, elbow instance). Generated rigs put the elbow joint FORWARD of the arm
 * tube's flesh centre (measured 2026-07-28 across five specimens: android
 * 19–21mm on a 131mm forearm ≈ 15%, barbarian 9–13mm, stylised female 7–11mm;
 * an anime specimen whose hands deform well is near-centred at 3–7mm). A
 * forward pivot folds flexion through the wrong flesh — uneven crease, pinch,
 * and the noodle-arm silhouette.
 *
 * The cure is the SAME tier-2 door the wrist uses (recenterBoneBind + its
 * scene twin): rest and inverse-bind move together, so the mesh is bit-stable
 * and only the rotation pivot moves.
 *
 * TWO DELIBERATE CHOICES:
 *
 * 1. PERPENDICULAR ONLY. The joint keeps its station ALONG the limb — Allen's
 *    wrist spec verbatim, "same length location, centred in the volume".
 *    Sliding a joint along its own bone would change bone LENGTH, which is a
 *    different (and unasked-for) edit, and would break the child's offset.
 *
 * 2. GEOMETRY, NOT WEIGHTS. The capture is every vertex in a cylinder about
 *    the joint, whatever bone owns it. Elbow flesh is exactly where skin
 *    weights are least trustworthy — on the Tripo barbarian the elbow-region
 *    vertices are 100% owned by TWIST helpers, so a weight-filtered capture
 *    would sample the wrong set or nothing at all. The tube's centre is a fact
 *    about the mesh, so it is measured from the mesh.
 *
 * The cylinder and the mean-shift are both load-bearing, and both were forced
 * by a synthetic tube whose true centre was known: a single spherical capture
 * read a 20.0mm offset as 4.3mm, and iterating a SPHERE still settled at
 * 5.2mm, because a sphere clips a longer stretch of the tube's near wall than
 * its far wall. Neither error would have been visible on a real specimen,
 * where any plausible number reads as a result.
 *
 * Depends on: math/vec3. Depended on by: the app's elbow evidence + the
 * recentre door. No engine, no voxel grid (§ this method is raw-vertex by
 * design — the wrist's seed-centroid needs the graft's grid; an elbow has no
 * cut to seed from).
 */
import { KernelError } from '../ir/errors.js';
import { vec3Add, vec3Dot, vec3Length, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';

/** A limb whose flesh cannot be measured says so — never a silent no-op. */
export class LimbAxisRefusal extends KernelError {
  constructor(readonly reason: string) {
    super(`limb-axis recentre: ${reason}`);
  }
}

export interface LimbAxisResult {
  /** Where the joint SHOULD sit: its own station on the limb, centred in the flesh. */
  readonly centre: Vec3Data;
  /** How far that is from where the joint sits now, perpendicular to the limb. */
  readonly offset: number;
  /** Vertices the measurement actually used — evidence, never assumed. */
  readonly sampled: number;
  /** The capture radius used, so a reader can judge the sample's meaning. */
  readonly radius: number;
}

/** Vertices closer to the joint than this fraction of the limb are the tube. */
export const CAPTURE_FRACTION = 0.45;
/** Below this many captured vertices the centroid is noise, not a centre. */
export const MIN_SAMPLES = 24;
/** Axial half-band of the capture cylinder, as a fraction of the limb. */
export const BAND_FRACTION = 0.25;
/** Mean-shift steps — convergence is fast; the cap is a runaway guard. */
export const MAX_SHIFT_STEPS = 12;
/** Converged when a step moves less than this fraction of the limb. */
export const SHIFT_TOLERANCE = 1e-4;

export interface LimbFrame {
  /** Where the joint sits now, in world bind space. */
  readonly joint: Vec3Data;
  /** The limb line the capture is measured against (unnormalised). */
  readonly axis: Vec3Data;
  /** The scale every capture distance is expressed in. */
  readonly limbLength: number;
  readonly parentBone: string;
  /** Null when the joint's anatomical child is absent — see the wrist note. */
  readonly childBone: string | null;
}

/**
 * The limb a joint belongs to, resolved through the ONTOLOGY rather than the
 * bone tree — the anatomical child is a fact about the skeleton's MEANING, and
 * the tree does not know it. Walking the tree instead picks up the TWIST
 * SUPPLEMENTS that current-generation rigs hang on the elbow, which sit
 * essentially on the joint and make the limb zero-length (measured: the
 * android resolved a capture radius of 0.0000).
 *
 * THE WRIST FALLBACK. `LeftHand`'s declared primary child is
 * `LeftHandMiddle1`, so on a fingerless rig — the norm for generated
 * characters before a graft — the wrist has no mapped child at all. It still
 * has a limb: the FOREARM arriving at it. So when the child is missing the
 * axis becomes parent→joint and the scale becomes the parent segment's own
 * length. That is anatomically the right tube for a wrist either way; the
 * child is used when present because a two-ended line is the steadier axis,
 * and the joint being measured must not define its own direction.
 */
export function resolveLimb(
  rig: { bones: readonly { name: string; parent: string | null; worldBind: { position: Vec3Data } }[]; mapping: readonly { rigBone: string; ontologyId: string }[] },
  ontology: { byId: ReadonlyMap<string, { parent: string | null; primaryChild: string | null }> },
  boneName: string,
): LimbFrame {
  const bone = rig.bones.find((b) => b.name === boneName);
  if (bone === undefined) throw new LimbAxisRefusal(`this rig has no bone "${boneName}"`);
  const ontologyId = rig.mapping.find((m) => m.rigBone === boneName)?.ontologyId;
  const spec = ontologyId === undefined ? undefined : ontology.byId.get(ontologyId);
  if (ontologyId === undefined || spec === undefined) {
    throw new LimbAxisRefusal(`"${boneName}" is not mapped to the ontology, so its limb cannot be named`);
  }
  if (spec.parent === null) {
    throw new LimbAxisRefusal(`"${ontologyId}" declares no parent — a limb needs a segment arriving at the joint`);
  }
  const rigBoneFor = (id: string): { name: string; worldBind: { position: Vec3Data } } | undefined => {
    const name = rig.mapping.find((m) => m.ontologyId === id)?.rigBone;
    return name === undefined ? undefined : rig.bones.find((b) => b.name === name);
  };
  const parent = rigBoneFor(spec.parent);
  if (parent === undefined) {
    throw new LimbAxisRefusal(`this rig does not map "${spec.parent}", the segment arriving at "${boneName}"`);
  }
  const child = spec.primaryChild === null ? undefined : rigBoneFor(spec.primaryChild);
  const j = bone.worldBind.position;
  const p = parent.worldBind.position;
  if (child === undefined) {
    return {
      joint: j,
      axis: { x: j.x - p.x, y: j.y - p.y, z: j.z - p.z },
      limbLength: Math.hypot(j.x - p.x, j.y - p.y, j.z - p.z),
      parentBone: parent.name,
      childBone: null,
    };
  }
  const c = child.worldBind.position;
  return {
    joint: j,
    axis: { x: c.x - p.x, y: c.y - p.y, z: c.z - p.z },
    limbLength: Math.hypot(c.x - j.x, c.y - j.y, c.z - j.z),
    parentBone: parent.name,
    childBone: child.name,
  };
}

/**
 * The joint's flesh-centred position: capture the tube around the joint, take
 * its centroid, and keep only the component PERPENDICULAR to the limb axis.
 *
 * `axis` need not be normalised. `limbLength` sets the capture radius, so the
 * measurement scales with the character rather than assuming human metres.
 */
export function limbAxisCentre(
  vertices: readonly Vec3Data[],
  joint: Vec3Data,
  axis: Vec3Data,
  limbLength: number,
): LimbAxisResult {
  const axisLength = vec3Length(axis);
  if (!(axisLength > 0)) {
    throw new LimbAxisRefusal('the limb axis has no direction — parent and child are co-located');
  }
  if (!(limbLength > 0) || !Number.isFinite(limbLength)) {
    throw new LimbAxisRefusal(`limb length ${limbLength} is not a positive measurement`);
  }
  const unit = vec3Scale(axis, 1 / axisLength);
  const radius = limbLength * CAPTURE_FRACTION;

  const halfBand = limbLength * BAND_FRACTION;

  /**
   * Capture a CYLINDER about `probe`, not a sphere: an axial band decides
   * WHICH slice of the limb is sampled, and a perpendicular limit rejects the
   * torso or the other arm. A sphere cannot do this job — it clips a longer
   * stretch of the near wall than the far wall, so it converges on a biased
   * fixed point (measured: a 20.0mm offset settling at 5.2mm). The cylinder's
   * capture is symmetric in the perpendicular plane once the probe reaches the
   * tube's axis, which makes the true centre the fixed point.
   */
  const centroidAbout = (probe: Vec3Data): { c: Vec3Data; n: number } | null => {
    let sx = 0;
    let sy = 0;
    let sz = 0;
    let n = 0;
    for (const v of vertices) {
      const d = vec3Sub(v, probe);
      const along = vec3Dot(d, unit);
      if (Math.abs(along) > halfBand) continue;
      if (vec3Length(vec3Sub(d, vec3Scale(unit, along))) > radius) continue;
      sx += v.x;
      sy += v.y;
      sz += v.z;
      n += 1;
    }
    return n === 0 ? null : { c: { x: sx / n, y: sy / n, z: sz / n }, n };
  };

  // MEAN-SHIFT, and it is not optional. A capture sphere centred on the
  // joint is biased TOWARD the joint: when the joint sits off-axis the far
  // wall of the tube falls outside the sphere while the near wall stays in,
  // so a single pass under-corrects. Measured on a synthetic tube whose
  // answer is known: a 20.0mm offset read as 4.3mm — an error invisible on a
  // real specimen, where any plausible number looks like a result. Iterating
  // the capture about the running centre converges on the tube's own axis.
  let centre = joint;
  let sampled = 0;
  for (let step = 0; step < MAX_SHIFT_STEPS; step += 1) {
    const found = centroidAbout(centre);
    if (found === null) break;
    sampled = found.n;
    // The joint keeps its STATION: measure the shift perpendicular to the
    // limb only, always relative to the joint's own place on the bone.
    const delta = vec3Sub(found.c, joint);
    const perpendicular = vec3Sub(delta, vec3Scale(unit, vec3Dot(delta, unit)));
    const next = vec3Add(joint, perpendicular);
    const moved = vec3Length(vec3Sub(next, centre));
    centre = next;
    if (moved <= limbLength * SHIFT_TOLERANCE) break;
  }
  if (sampled < MIN_SAMPLES) {
    throw new LimbAxisRefusal(
      `only ${sampled} vertices within ${radius.toFixed(4)} of the joint (need ${MIN_SAMPLES}) — ` +
        `too little flesh to name a centre`,
    );
  }
  const perpendicular = vec3Sub(centre, joint);
  return { centre, offset: vec3Length(perpendicular), sampled, radius };
}
